import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Play, Info, Star } from "lucide-react";
import { useAllItems } from "@/lib/library-store";
import { useTheme } from "@/lib/theme-context";
import { smallImage, tmdbBackdropSrcSet, picsumResize, isExternalRemoteImage, weservProxy, weservSrcSet } from "@/lib/image-utils";
import { pickDuration, pickGenres, pickOverview, pickTitle } from "@/lib/i18n";
import type { LanguageCode } from "@/lib/theme-context";
import { useIsHandheld } from "@/hooks/use-device";

function backdropFor(url: string) {
  // Build responsive sources for TMDB/picsum backdrops.
  if (!url) return { src: url, srcSet: undefined as string | undefined };
  if (url.includes("image.tmdb.org")) {
    // Serve the w500 variant as the base src so mobile devices (390–430 CSS
    // px × 2 dpr ≈ 780–860 physical) never fetch the 1280px asset for the
    // LCP paint. The srcSet still exposes w780/w1280 for wide screens.
    return { src: smallImage(url, 500, 281), srcSet: tmdbBackdropSrcSet(url) };
  }
  if (url.includes("picsum.photos")) {
    return {
      src: picsumResize(url, 780, 439),
      srcSet: [
        `${picsumResize(url, 480, 270)} 480w`,
        `${picsumResize(url, 800, 450)} 800w`,
        `${picsumResize(url, 1280, 720)} 1280w`,
      ].join(", "),
    };
  }
  if (isExternalRemoteImage(url)) {
    return { src: weservProxy(url, 800), srcSet: weservSrcSet(url, [480, 800, 1280]) };
  }
  return { src: url, srcSet: undefined };
}

export function Hero() {
  const items = useAllItems();
  const isHandheld = useIsHandheld();
  const trending = items.filter((m) => m.isTrending);
  const featured = trending.length > 0 ? trending : items.slice(0, 5);
  const [idx, setIdx] = useState(0);
  // Render only the active backdrop at first; mount the others after user
  // interacts or browser is idle — saves ~1MB of image bytes on initial paint.
  const [renderAll, setRenderAll] = useState(false);
  const { language } = useTheme();
  const lang: LanguageCode = language;

  useEffect(() => {
    if (renderAll || featured.length <= 1) return;
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    const enable = () => setRenderAll(true);
    const id = w.requestIdleCallback
      ? w.requestIdleCallback(enable, { timeout: 4000 })
      : window.setTimeout(enable, 3000);
    const onUserAction = () => setRenderAll(true);
    window.addEventListener("pointerdown", onUserAction, { once: true, passive: true });
    window.addEventListener("scroll", onUserAction, { once: true, passive: true });
    return () => {
      if (typeof id === "number") window.clearTimeout(id);
      window.removeEventListener("pointerdown", onUserAction);
      window.removeEventListener("scroll", onUserAction);
    };
  }, [renderAll, featured.length]);

  useEffect(() => {
    if (!renderAll || featured.length <= 1) return;
    // Respect prefers-reduced-motion: do not autoplay the slideshow.
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (mq?.matches) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % featured.length), 7000);
    return () => clearInterval(t);
  }, [featured.length, renderAll]);

  const movie = featured[idx];
  if (!movie) return null;

  const visibleBackdrops = renderAll ? featured : [featured[idx]];

  return (
    <section className="relative h-[440px] sm:h-[70vh] sm:min-h-[480px] sm:max-h-[720px] overflow-hidden">
      {/* Backdrops — lazy until idle */}
      {visibleBackdrops.map((m) => {
        const realIdx = featured.indexOf(m);
        const { src, srcSet } = backdropFor(m.backdrop);
        const isActive = realIdx === idx;
        const isLcp = realIdx === 0;
        return (
          <img
            key={m.id}
            src={src}
            srcSet={srcSet}
            sizes="100vw"
            alt=""
            aria-hidden
            width={1280}
            height={720}
            loading={isLcp ? "eager" : "lazy"}
            decoding={isLcp ? "sync" : "async"}
            fetchPriority={isLcp ? "high" : "low"}
            onError={(e) => {
              const el = e.currentTarget;
              if (m.poster && el.src !== m.poster) el.src = m.poster;
              else el.style.display = "none";
            }}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${isActive ? "opacity-100" : "opacity-0"}`}
          />
        );
      })}
      {/* Always-dark overlay so white text stays readable in light mode */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/55 to-black/20" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/35 to-transparent" />
      {/* Tiny fade into page background at very bottom only */}
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background to-transparent" />
      {/* Content */}
      <div className="relative h-full container mx-auto px-4 flex items-end pb-12">
        <div key={movie.id} className="max-w-2xl animate-fade-in">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-[11px] font-black px-2 py-0.5 rounded-md bg-white text-black uppercase tracking-wider shadow">
              {(language === "ar" ? "\u0645\u0645\u064a\u0632" : language === "fr" ? "En vedette" : "Featured")}
            </span>
            <span className="text-sm text-white/90">{movie.year}</span>
            <span className="flex items-center gap-1 text-sm font-bold text-white">
              <Star className="h-3.5 w-3.5 fill-[var(--rating)] text-[var(--rating)]" />
              {movie.rating}
            </span>
            <span className="text-sm text-white/90">{pickDuration(movie, lang)}</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold leading-tight text-white drop-shadow-2xl">
            {pickTitle(movie, lang)}
          </h1>
          <div className="flex flex-wrap gap-2 mt-3">
            {pickGenres(movie, lang).map((g) => (
              <span key={g} className="text-xs px-2.5 py-1 rounded-full glass text-foreground">{g}</span>
            ))}
          </div>
          <p className="mt-4 text-base md:text-lg text-white/85 line-clamp-3 max-w-xl leading-relaxed">
            {pickOverview(movie, lang)}
          </p>
          <div className="flex flex-wrap gap-2 sm:gap-3 mt-6">
            {/* Mobile/tablet: Watch Now → scrolls to #watch. Hidden on real desktops via JS hook + CSS fallback. */}
            {isHandheld && (
              <Link to={movie.type === "series" ? "/series/$slug" : "/movie/$slug"} params={{ slug: movie.slug }} hash="watch" className="btn-primary inline-flex items-center gap-2 text-sm px-4 py-2">
                <Play className="h-4 w-4 fill-current" />
                {(language === "ar" ? "\u0634\u0627\u0647\u062f \u0627\u0644\u0622\u0646" : language === "fr" ? "Regarder maintenant" : "Watch Now")}
              </Link>
            )}
            {/* Desktop: Read article → scrolls to #article and opens collapsibles */}
            {!isHandheld && (
              <Link to={movie.type === "series" ? "/series/$slug" : "/movie/$slug"} params={{ slug: movie.slug }} hash="article" className="btn-primary inline-flex items-center gap-2 text-sm sm:text-base">
                <Info className="h-4 w-4" />
                {(language === "ar" ? "\u0627\u0642\u0631\u0623 \u0627\u0644\u0645\u0642\u0627\u0644" : language === "fr" ? "Lire l’article" : "Read article")}
              </Link>
            )}
            <Link to={movie.type === "series" ? "/series/$slug" : "/movie/$slug"} params={{ slug: movie.slug }} className="inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg glass-strong text-foreground font-semibold hover:bg-accent transition text-sm sm:text-base">
              <Info className="h-4 w-4" />
              {(language === "ar" ? "\u0627\u0644\u062a\u0641\u0627\u0635\u064a\u0644" : language === "fr" ? "Détails" : "Details")}
            </Link>
          </div>
          {/* Indicator dots */}
          <div className="flex gap-2 mt-8">
            {featured.map((_, i) => (
              <button
                key={i}
                onClick={() => { setRenderAll(true); setIdx(i); }}
                className={`relative inline-flex items-center justify-center h-6 min-w-6 p-2 rounded-full transition-all before:content-[""] before:block before:h-1.5 before:rounded-full before:transition-all ${i === idx ? "before:w-8 before:bg-primary" : "before:w-4 before:bg-white/40 hover:before:bg-white/70"}`}
                aria-label={language === "ar" ? `الشريحة ${i + 1}` : language === "fr" ? `Diapositive ${i + 1}` : `Slide ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
